import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/providers/auth.provider';
import {
  SubscriptionLevel,
  hasFeatureAccess,
  getFeatureAccess,
  getAvailableFeatures,
  getUpgradeRequiredFeatures,
  getSubscriptionLimits,
  hasReachedLimit,
  getRemainingLimit,
  getLimitText as getLimitTextRaw,
  SubscriptionLimits,
  FEATURES
} from '@/types/subscription.types';

export function useSubscription() {
  const { user } = useAuth();
  const { t } = useTranslation();

  // Get subscription level from user's account, default to 'free' if not available
  const subscriptionLevel: SubscriptionLevel = (user?.account?.subscriptionLevel as SubscriptionLevel) || 'free';

  return useMemo(() => ({
    level: subscriptionLevel,
    hasFeature: (featureId: string) => hasFeatureAccess(subscriptionLevel, featureId),
    getFeatureAccess: (featureId: string) => getFeatureAccess(subscriptionLevel, featureId),
    availableFeatures: getAvailableFeatures(subscriptionLevel),
    upgradeRequiredFeatures: getUpgradeRequiredFeatures(subscriptionLevel),
    canAccess: (featureId: string) => hasFeatureAccess(subscriptionLevel, featureId),
    limits: getSubscriptionLimits(subscriptionLevel),
    hasReachedLimit: (limitType: keyof SubscriptionLimits, currentCount: number) => 
      hasReachedLimit(subscriptionLevel, limitType, currentCount),
    getRemainingLimit: (limitType: keyof SubscriptionLimits, currentCount: number) => 
      getRemainingLimit(subscriptionLevel, limitType, currentCount),
    getLimitText: (limitType: keyof SubscriptionLimits, currentCount: number) => {
      const rawText = getLimitTextRaw(subscriptionLevel, limitType, currentCount);
      if (rawText === 'customers.limits.unlimited') {
        return t(rawText);
      }
      // Parse the format: key:param1:param2:param3:param4
      const parts = rawText.split(':');
      if (parts.length === 5) {
        const [key, used, total, remaining, remainingKey] = parts;
        if (key && used && total && remaining && remainingKey) {
          return t(key, { used, total, remaining, remainingText: t(remainingKey) });
        }
      }
      return rawText;
    },
  }), [subscriptionLevel, t]);
}

export function useFeature(featureId: string) {
  const { hasFeature, getFeatureAccess } = useSubscription();

  return useMemo(() => ({
    hasAccess: hasFeature(featureId),
    access: getFeatureAccess(featureId),
    feature: FEATURES[featureId],
  }), [featureId, hasFeature, getFeatureAccess]);
}